import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Stack, Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';

import { getAnonymizationProfiles } from '../../services/anonymizationProfilesService';
import ProfilesTab from '../shared/ProfilesTab/ProfilesTab'; // Reusable profiles tab component
import DicomTagSelector from '../shared/DicomTagSelector'; // Adjust path as needed

const DicomOptionsDialog = ({
  open, options, onChangeOptions, onClose, onConfirm, loading
}) => {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState('');
  const [tab, setTab] = useState('options');

  useEffect(() => {
    if (!open) return;
    const loadProfiles = async () => {
      try {
        setProfilesLoading(true);
        const data = await getAnonymizationProfiles('dicom_pseudonymization');
        setProfiles(data || []);
      } catch (err) {
        setProfilesError(t('DicomOptionsDialog.profilesLoadError', 'Nepodarilo sa načítať profily.'));
      } finally {
        setProfilesLoading(false);
      }
    };
    loadProfiles();
  }, [open, t]);

  useEffect(() => {
    if (open) setTab('options');
  }, [open]);

  const handleTabChange = (_event, value) => {
    setTab(value);
    if (value === 'options') {
      onChangeOptions({ ...options, profileId: null });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('DicomOptionsDialog.title', 'Možnosti DICOM Pseudonymizácie')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t('DicomOptionsDialog.subtitle', 'Vyberte, ktoré metadáta sa majú bezpečne nahradiť pseudonymom.')}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5, pb: 2, bgcolor: 'background.default' }}>
        <Box sx={{ mb: 2 }}>
          <Tabs 
            value={tab} 
            onChange={handleTabChange} 
            variant="fullWidth" 
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 40 }
            }}
          >
            <Tab value="options" label={t('admin.pii.options.tab.custom', 'Vlastné nastavenia')} />
            <Tab value="profiles" label={t('admin.pii.options.tab.profiles', 'Uložené Profily')} />
          </Tabs>
        </Box>

        {tab === 'profiles' && (
          <ProfilesTab
            profiles={profiles}
            loading={profilesLoading}
            error={profilesError}
            selectedProfileId={options.profileId || null}
            onSelectProfile={(profileId) => onChangeOptions({ ...options, profileId })}
          />
        )}

        {tab === 'options' && (
           <DicomTagSelector options={options} onChangeOptions={onChangeOptions} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ fontWeight: 600, borderRadius: 2 }}
        >
          {t('common.cancel', 'Zrušiť')}
        </Button>
        <Button 
          variant="contained" 
          onClick={onConfirm} 
          disabled={loading} 
          startIcon={<PlayArrowIcon />}
          disableElevation
          sx={{ fontWeight: 600, borderRadius: 2 }}
        >
          {t('admin.pii.startPseudonymization', 'Spustiť pseudonymizáciu')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DicomOptionsDialog;